import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { Radio, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
    GET_INHUB_SORTING_GROUP_COMPARISON,
    type GetInhubSortingGroupComparisonResponse
} from '../graphql/queries/getInhubSortingGroupComparison';
import type { InhubSortingDimension, InhubSortingMetric } from '../graphql/types';

const BAR_COLORS = ['#5B8FF9', '#5AD8A6', '#5D7092', '#F6BD16', '#E8684A', '#6DC8EC'];
const COMPARISON_TOP_N = 6;

interface Props {
    hubId: number;
    rangeStart: string;
    rangeEnd: string;
    metric: InhubSortingMetric;
    metricLabel: string;
}

const GroupComparisonChart: React.FC<Props> = ({ hubId, rangeStart, rangeEnd, metric, metricLabel }) => {
    const { t } = useTranslation();
    const I18N = 'wms.delivery.inhubDashboard.sorting';
    const [dimension, setDimension] = useState<InhubSortingDimension>('ACCOUNT');

    const { data, loading } = useQuery<GetInhubSortingGroupComparisonResponse>(GET_INHUB_SORTING_GROUP_COMPARISON, {
        variables: { filter: { hubId, rangeStart, rangeEnd, metric, dimension } },
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true
    });

    const chartData = useMemo(() => {
        const bars = data?.getInhubSortingGroupComparison?.bars ?? [];
        const unGroupedLabel = t('wms.delivery.inhubDashboard.unGrouped');
        return bars.map((b) => ({
            name: (b.name === '' || b.name === '未分组') ? unGroupedLabel : b.name,
            value: b.value
        }));
    }, [data, t]);

    return (
        <div className="border border-gray-200 rounded-lg p-3 flex flex-col gap-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800">{t(`${I18N}.comparison.title`)}</span>
                    <span className="text-xs text-gray-700">· {metricLabel}</span>
                    <span className="text-xs text-gray-500">· {t(`${I18N}.topNHint`, { topN: COMPARISON_TOP_N })}</span>
                </div>
                <Radio.Group
                    value={dimension}
                    onChange={(e) => setDimension(e.target.value)}
                    size="small"
                >
                    <Radio.Button value="ACCOUNT">{t(`${I18N}.dimension.byAccount`)}</Radio.Button>
                    <Radio.Button value="TEAM">{t(`${I18N}.dimension.byTeam`)}</Radio.Button>
                </Radio.Group>
            </div>
            <div className="h-64">
                {loading && chartData.length === 0 ? (
                    <Skeleton active paragraph={{ rows: 4 }} />
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 40, left: 20, bottom: 0 }}>
                            <XAxis type="number" hide />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: '#262626' }} width={120} />
                            <Tooltip cursor={{ fill: '#fafafa' }} />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                {chartData.map((_, idx) => (
                                    <Cell key={idx} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                                ))}
                                <LabelList
                                    dataKey="value"
                                    position="right"
                                    style={{ fontSize: 11, fill: '#262626' }}
                                    formatter={(v) => (v == null ? '' : Number(v).toLocaleString())}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default GroupComparisonChart;
